import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  CreditCard, Plus, Pencil, Trash2, Check, X, Building2,
  Calendar, CheckCircle2, Clock, AlertTriangle, ArrowRight,
  TrendingDown, ShoppingBag, DollarSign, Wallet2, Sparkles,
  ChevronRight, RefreshCw, Eye, ShieldAlert, BadgeCheck, FileUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { useTenant } from '../contexts/TenantContext';
import { formatCurrency, formatDate } from '../utils/format';
import TabsNav from '../components/TabsNav';
import MelhorCartao from '../components/MelhorCartao';
import { CardBrandIcon, BandeiraSelect, bandeiraLabel } from '../components/CardBrandIcon';
import LancamentoModal from '../components/LancamentoModal';
import ImportarFaturaModal from '../components/ImportarFaturaModal';
import { parseValor } from '../utils/csvUtils';

// ── BANDEIRAS E CORES ────────────────────────────────────────────────────────
const PALETTE = [
  '#6366F1', '#820AD1', '#FF6200', '#CC0000', '#0147a0',
  '#00A651', '#242424', '#06b6d4', '#d97706', '#ec4899',
  '#4f46e5', '#0f766e', '#1e293b'
];

function formatMesReferencia(ref) {
  if (!ref) return '-';
  try {
    const [year, month] = ref.split('-');
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    const monthName = date.toLocaleDateString('pt-BR', { month: 'long' });
    return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} / ${year}`;
  } catch {
    return ref;
  }
}

// Faturas de uma aba. Abertas: da mais próxima para a mais distante (a atual vem primeiro).
// Fechadas e histórico: da mais recente para a mais antiga.
function ordenaFaturas(list, tab) {
  const base = tab === 'abertas'
    ? list.filter((f) => f.status === 'aberta')
    : list.filter((f) => f.status === 'fechada' || f.status === 'paga');
  return [...base].sort((a, b) => (tab === 'abertas'
    ? a.faturaReferencia.localeCompare(b.faturaReferencia)
    : b.faturaReferencia.localeCompare(a.faturaReferencia)));
}

// ── MODAL NOVO / EDITAR CARTÃO ───────────────────────────────────────────────
function CartaoModal({ isOpen, initial, carteiras, onSave, onClose }) {
  const [nome, setNome] = useState('');
  const [bandeira, setBandeira] = useState('mastercard');
  const [limite, setLimite] = useState('');
  const [diaFechamento, setDiaFechamento] = useState(20);
  const [diaVencimento, setDiaVencimento] = useState(27);
  const [cor, setCor] = useState('#6366F1');
  const [carteiraDebitoId, setCarteiraDebitoId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNome(initial?.nome || '');
      setBandeira(initial?.bandeira || 'mastercard');
      setLimite(initial?.limite != null ? String(initial.limite) : '');
      setDiaFechamento(initial?.diaFechamento || 20);
      setDiaVencimento(initial?.diaVencimento || 27);
      setCor(initial?.cor || '#6366F1');
      setCarteiraDebitoId(initial?.carteiraDebitoId || '');
      setError('');
    }
  }, [isOpen, initial]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!nome.trim()) {
      setError('Por favor, informe o nome do cartão.');
      return;
    }
    const limNum = parseFloat(String(limite).replace(',', '.'));
    if (isNaN(limNum) || limNum < 0) {
      setError('Informe um limite válido (maior ou igual a zero).');
      return;
    }
    const fech = parseInt(diaFechamento, 10);
    const venc = parseInt(diaVencimento, 10);
    if (isNaN(fech) || fech < 1 || fech > 28) {
      setError('O dia de fechamento deve ser entre 1 e 28.');
      return;
    }
    if (isNaN(venc) || venc < 1 || venc > 28) {
      setError('O dia de vencimento deve ser entre 1 e 28.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await onSave({
        nome: nome.trim(),
        bandeira,
        limite: limNum,
        diaFechamento: fech,
        diaVencimento: venc,
        cor,
        carteiraDebitoId: carteiraDebitoId || null,
        ativo: true,
      });
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erro ao salvar cartão.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="cartao-modal-overlay"
    >
      <div
        className="nexus-modal w-full max-w-lg flex flex-col my-auto max-h-[92vh] overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
        data-testid="cartao-modal"
      >
        {/* Faixa decorativa com a cor selecionada */}
        <div className="h-1.5 transition-colors duration-200" style={{ background: cor }} />

        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm flex-shrink-0"
              style={{ background: cor }}
            >
              <CreditCard size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
                {initial ? 'Editar Cartão de Crédito' : 'Novo Cartão de Crédito'}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Configure fatura própria, dias de fechamento e limite
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl text-xs font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
              <ShieldAlert size={15} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Modo de Sincronização */}
          <div>
            <label className="fin-label text-xs">Modo de Gestão</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div className="p-2.5 rounded-xl border-2 border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/40 text-xs font-bold text-indigo-700 dark:text-indigo-300 flex items-center justify-between">
                <span>Manual & Faturas</span>
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
              </div>
              <div
                className="p-2.5 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-800/40 text-xs font-semibold text-slate-400 flex items-center justify-between cursor-not-allowed select-none opacity-70"
                title="Sincronização bancária automática em fase de homologação"
              >
                <span>Conexão Direta (Open Finance)</span>
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500">
                  Em breve
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="fin-label">Nome do Cartão *</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Nubank Ultravioleta, Itaú Black, Inter..."
              className="fin-input"
              data-testid="input-cartao-nome"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="fin-label">Bandeira *</label>
              <BandeiraSelect value={bandeira} onChange={setBandeira} testId="select-cartao-bandeira" />
            </div>
            <div>
              <label className="fin-label">Limite Total (R$) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={limite}
                onChange={(e) => setLimite(e.target.value)}
                placeholder="0,00"
                className="fin-input"
                data-testid="input-cartao-limite"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="fin-label">Dia de Fechamento (1-28) *</label>
              <input
                type="number"
                min="1"
                max="28"
                value={diaFechamento}
                onChange={(e) => setDiaFechamento(e.target.value)}
                className="fin-input"
                data-testid="input-cartao-fechamento"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Compras após esse dia caem no mês seguinte.
              </span>
            </div>
            <div>
              <label className="fin-label">Dia de Vencimento (1-28) *</label>
              <input
                type="number"
                min="1"
                max="28"
                value={diaVencimento}
                onChange={(e) => setDiaVencimento(e.target.value)}
                className="fin-input"
                data-testid="input-cartao-vencimento"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Prazo final para pagamento da fatura.
              </span>
            </div>
          </div>

          <div>
            <label className="fin-label">Carteira de Débito (Opcional)</label>
            <select
              value={carteiraDebitoId}
              onChange={(e) => setCarteiraDebitoId(e.target.value)}
              className="fin-input"
              data-testid="select-cartao-carteira-debito"
            >
              <option value="">Nenhuma vinculada (escolher na hora de pagar)</option>
              {carteiras.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} {c.conta ? `(${c.conta})` : ''}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-slate-400 mt-1 block">
              Conta de onde a fatura será debitada automaticamente ao ser paga.
            </span>
          </div>

          <div>
            <label className="fin-label">Cor de Identificação</label>
            <div className="flex flex-wrap gap-2 pt-1">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCor(c)}
                  className={`w-7 h-7 rounded-full transition-transform cursor-pointer flex items-center justify-center ${
                    cor === c ? 'scale-115 ring-2 ring-offset-2 ring-indigo-500' : 'hover:scale-105'
                  }`}
                  style={{ background: c }}
                >
                  {cor === c && <Check size={14} className="text-white" />}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 text-xs font-bold rounded-xl text-white shadow-md transition-all duration-150 flex items-center gap-1.5"
              style={{ background: cor }}
              data-testid="btn-salvar-cartao"
            >
              {loading && <RefreshCw size={14} className="animate-spin" />}
              <span>{initial ? 'Salvar Alterações' : 'Criar Cartão'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── MODAL DE PAGAMENTO DE FATURA ─────────────────────────────────────────────
export function PagarFaturaModal({ isOpen, cartao, fatura, carteiras, onConfirm, onDesfazer, onClose }) {
  const [carteiraId, setCarteiraId] = useState('');
  const [opcao, setOpcao] = useState('total');      // total | parcial | minimo
  const [valorTexto, setValorTexto] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCarteiraId(cartao?.carteiraDebitoId || (carteiras[0]?.id || ''));
      setOpcao('total');
      setValorTexto('');
    }
  }, [isOpen, cartao, carteiras]);

  if (!isOpen || !fatura) return null;

  const fechada = fatura.status === 'fechada';
  const restante = Number(fatura.restante ?? fatura.totalPendente ?? fatura.total ?? 0);
  const minimo = Number(fatura.minimo ?? 0);
  const sobraMinimo = Math.max(0, Math.round((restante - minimo) * 100) / 100);
  const valorParcial = parseValor(valorTexto);
  const parcialInvalido = opcao === 'parcial'
    && (Number.isNaN(valorParcial) || valorParcial <= 0 || valorParcial > restante + 0.004);
  const pagaTudo = opcao === 'parcial' && !parcialInvalido && Math.abs(valorParcial - restante) < 0.005;
  const pendentes = (fatura.pagamentos || []).filter((p) => !p.consolidado);

  const valorDaAcao = opcao === 'minimo' ? minimo : opcao === 'parcial' ? (parcialInvalido ? 0 : valorParcial) : restante;
  const rotuloBotao = opcao === 'minimo' ? `Pagar mínimo ${formatCurrency(minimo)}`
    : opcao === 'parcial' ? (parcialInvalido ? 'Informe o valor' : pagaTudo ? `Pagar total ${formatCurrency(restante)}` : `Pagar ${formatCurrency(valorParcial)}`)
    : `Pagar ${formatCurrency(restante)}`;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (parcialInvalido) return;
    setLoading(true);
    try {
      await onConfirm({
        carteiraDebitoId: carteiraId || null,
        modo: pagaTudo ? 'total' : opcao,
        valor: opcao === 'parcial' ? Math.round(valorParcial * 100) / 100 : null,
      });
      onClose();
    } catch {
      /* o erro já foi mostrado ao usuário; o modal continua aberto para ele corrigir */
    } finally {
      setLoading(false);
    }
  };

  const Opcao =({ id, titulo, detalhe, destaque }) => (
    <label
      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
        opcao === id
          ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/30'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
      }`}
    >
      <input type="radio" name="opcao-pagamento" checked={opcao === id} onChange={() => setOpcao(id)} className="mt-1" data-testid={`opcao-${id}`} />
      <span className="flex-1 min-w-0">
        <span className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{titulo}</span>
          {destaque && <span className="text-sm font-extrabold text-slate-900 dark:text-white whitespace-nowrap">{destaque}</span>}
        </span>
        {detalhe && <span className="text-[11px] text-slate-500 dark:text-slate-400 block mt-0.5">{detalhe}</span>}
      </span>
    </label>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="nexus-modal w-full max-w-md flex flex-col my-auto rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="pagar-fatura-modal"
      >
        <div className="h-1.5 bg-emerald-500" />
        <div className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
              <BadgeCheck size={22} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Pagar Fatura</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {cartao?.nome} — {formatMesReferencia(fatura.faturaReferencia)}
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-700/60 space-y-1.5">
            {Number(fatura.totalPago) > 0 && (
              <>
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>Total da fatura</span><span>{formatCurrency(fatura.total)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-emerald-600 dark:text-emerald-400">
                  <span>Já pago</span><span>− {formatCurrency(fatura.totalPago)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                {Number(fatura.totalPago) > 0 ? 'Falta pagar' : 'Total da Fatura'}
              </span>
              <span className="text-xl font-extrabold text-slate-900 dark:text-white" data-testid="pagar-restante">{formatCurrency(restante)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Opcao id="total" titulo={fechada ? 'Pagar o total' : 'Pagar tudo o que falta'} destaque={formatCurrency(restante)} />
            {fechada ? (
              <Opcao
                id="minimo"
                titulo={`Pagar o mínimo (${fatura.percentualMinimo ?? 15}%)`}
                destaque={formatCurrency(minimo)}
                detalhe={`Os ${formatCurrency(sobraMinimo)} restantes vão para a fatura de ${formatMesReferencia(fatura.proximaReferencia)} como "Saldo anterior".`}
              />
            ) : (
              <Opcao id="parcial" titulo="Pagar outro valor" detalhe="Antecipe parte da fatura e libere limite no cartão. Pode repetir até o fechamento." />
            )}
          </div>

          {opcao === 'parcial' && !fechada && (
            <div>
              <label className="fin-label">Quanto você quer pagar agora?</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoFocus
                  value={valorTexto}
                  onChange={(e) => setValorTexto(e.target.value.replace(/[^\d.,]/g, ''))}
                  placeholder="0,00"
                  className="fin-input pl-9"
                  data-testid="pagar-valor"
                />
              </div>
              <div className="flex gap-2 mt-2">
                {[0.25, 0.5, 0.75].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setValorTexto((Math.round(restante * f * 100) / 100).toFixed(2).replace('.', ','))}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  >
                    {Math.round(f * 100)}%
                  </button>
                ))}
              </div>
              {valorTexto && parcialInvalido && (
                <span className="text-[11px] text-rose-600 mt-1 block" data-testid="pagar-valor-erro">
                  {valorParcial > restante ? `O valor não pode passar do que falta (${formatCurrency(restante)}).` : 'Informe um valor maior que zero.'}
                </span>
              )}
              {pagaTudo && <span className="text-[11px] text-emerald-600 mt-1 block">Esse valor quita a fatura.</span>}
            </div>
          )}

          <div>
            <label className="fin-label">Debitar de qual carteira / conta?</label>
            <select value={carteiraId} onChange={(e) => setCarteiraId(e.target.value)} className="fin-input">
              <option value="">Apenas registrar o pagamento (sem gerar débito em conta)</option>
              {carteiras.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} {c.conta ? `(${c.conta})` : ''}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-slate-400 mt-1 block">
              Se selecionar uma carteira, será gerada uma despesa de {formatCurrency(valorDaAcao)} dessa conta.
            </span>
          </div>

          {pendentes.length > 0 && (
            <div className="rounded-xl border border-slate-200/70 dark:border-slate-700/60 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Pagamentos já feitos nesta fatura</p>
              <ul className="space-y-1">
                {pendentes.map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
                    <span>{formatDate(p.data)} · {p.tipo === 'minimo' ? 'mínimo' : 'parcial'}{p.carteiraNome ? ` · ${p.carteiraNome}` : ''}</span>
                    <span className="flex items-center gap-2">
                      <b>{formatCurrency(p.valor)}</b>
                      <button
                        type="button"
                        title="Desfazer este pagamento"
                        onClick={() => onDesfazer?.(p)}
                        className="text-slate-400 hover:text-rose-600"
                        data-testid={`desfazer-pagamento-${p.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pt-2 flex justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || parcialInvalido}
              className="px-5 py-2 text-xs font-bold rounded-xl text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md flex items-center gap-1.5 transition-all"
              data-testid="confirmar-pagamento"
            >
              {loading && <RefreshCw size={14} className="animate-spin" />}
              <span>{rotuloBotao}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── PÁGINA PRINCIPAL CARTÕES ────────────────────────────────────────────────
export default function CartaoCredito() {
  const { tenant } = useTenant();
  const accent = tenant?.corPrimaria || '#6366F1';

  const [cartoes, setCartoes] = useState([]);
  const [carteiras, setCarteiras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCartaoId, setSelectedCartaoId] = useState(null);

  // Faturas do cartão selecionado
  const [faturas, setFaturas] = useState([]);
  const [faturasLoading, setFaturasLoading] = useState(false);
  const [faturaTab, setFaturaTab] = useState('abertas'); // 'abertas' | 'fechadas'
  const [selectedRef, setSelectedRef] = useState(null);
  const [faturaDetalhe, setFaturaDetalhe] = useState(null);
  const [detalheLoading, setDetalheLoading] = useState(false);

  // Modais
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCartao, setEditingCartao] = useState(null);
  const [deletingCartao, setDeletingCartao] = useState(null);
  const [pagarModalData, setPagarModalData] = useState(null);
  const [novoLancamentoModalOpen, setNovoLancamentoModalOpen] = useState(false);
  const [importFaturaOpen, setImportFaturaOpen] = useState(false);

  // Carrega cartões e carteiras
  const loadCartoes = useCallback(async () => {
    setLoading(true);
    try {
      const [rCartoes, rCarteiras] = await Promise.all([
        api.get('/cartoes'),
        api.get('/carteiras'),
      ]);
      setCartoes(rCartoes.data || []);
      setCarteiras(rCarteiras.data || []);

      // Se nenhum selecionado, seleciona o primeiro
      if (!selectedCartaoId && rCartoes.data?.length > 0) {
        setSelectedCartaoId(rCartoes.data[0].id);
      }
    } catch {
      toast.error('Erro ao carregar cartões de crédito.');
    } finally {
      setLoading(false);
    }
  }, [selectedCartaoId]);

  useEffect(() => {
    loadCartoes();
  }, [loadCartoes]);

  // Carrega lista de faturas quando muda o cartão selecionado
  const loadFaturas = useCallback(async (cartaoId) => {
    if (!cartaoId) {
      setFaturas([]);
      return;
    }
    setFaturasLoading(true);
    try {
      const res = await api.get(`/cartoes/${cartaoId}/faturas`);
      const fatList = res.data || [];
      setFaturas(fatList);

      // Abre na fatura atual: a aberta mais próxima; sem abertas, a fechada mais recente
      const abertas = ordenaFaturas(fatList, 'abertas');
      const fechadas = ordenaFaturas(fatList, 'fechadas');
      if (abertas.length > 0) {
        setFaturaTab('abertas');
        setSelectedRef(abertas[0].faturaReferencia);
      } else if (fechadas.length > 0) {
        setFaturaTab('fechadas');
        setSelectedRef(fechadas[0].faturaReferencia);
      } else {
        setSelectedRef(null);
        setFaturaDetalhe(null);
      }
    } catch {
      toast.error('Erro ao carregar faturas do cartão.');
    } finally {
      setFaturasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCartaoId) {
      loadFaturas(selectedCartaoId);
    }
  }, [selectedCartaoId, loadFaturas]);

  // Carrega detalhe da fatura selecionada
  const loadFaturaDetalhe = useCallback(async (cartaoId, ref) => {
    if (!cartaoId || !ref) {
      setFaturaDetalhe(null);
      return;
    }
    setDetalheLoading(true);
    try {
      const res = await api.get(`/cartoes/${cartaoId}/faturas/${ref}`);
      setFaturaDetalhe(res.data);
    } catch {
      toast.error('Erro ao obter detalhes da fatura.');
    } finally {
      setDetalheLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCartaoId && selectedRef) {
      loadFaturaDetalhe(selectedCartaoId, selectedRef);
    }
  }, [selectedCartaoId, selectedRef, loadFaturaDetalhe]);

  const activeCartao = useMemo(
    () => cartoes.find((c) => c.id === selectedCartaoId) || cartoes[0],
    [cartoes, selectedCartaoId]
  );

  // Faturas filtradas por aba
  const faturasFiltradas = useMemo(() => {
    return ordenaFaturas(faturas, faturaTab);
  }, [faturas, faturaTab]);

  // Troca de aba de faturas
  const handleFaturaTabChange = (tabId) => {
    setFaturaTab(tabId);
    const subset = ordenaFaturas(faturas, tabId);
    if (subset.length > 0) {
      setSelectedRef(subset[0].faturaReferencia);
    } else {
      setSelectedRef(null);
      setFaturaDetalhe(null);
    }
  };

  // Salvar novo/editar cartão
  const handleSaveCartao = async (data) => {
    if (editingCartao) {
      await api.put(`/cartoes/${editingCartao.id}`, data);
      toast.success('Cartão atualizado com sucesso!');
    } else {
      const res = await api.post('/cartoes', data);
      toast.success('Cartão cadastrado com sucesso!');
      setSelectedCartaoId(res.data.id);
    }
    setEditingCartao(null);
    setModalOpen(false);
    loadCartoes();
  };

  // Excluir cartão
  const confirmDeleteCartao = async () => {
    if (!deletingCartao) return;
    try {
      await api.delete(`/cartoes/${deletingCartao.id}`);
      toast.success('Cartão removido com sucesso!');
      setDeletingCartao(null);
      setSelectedCartaoId(null);
      loadCartoes();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Não foi possível excluir o cartão.');
    }
  };

  // Pagar fatura
  const recarregarFatura = (referencia) => {
    loadCartoes();
    loadFaturas(activeCartao.id);
    loadFaturaDetalhe(activeCartao.id, referencia);
  };

  // payload: { carteiraDebitoId, modo: 'total' | 'parcial' | 'minimo', valor }
  const handlePagarFatura = async (payload) => {
    if (!activeCartao || !pagarModalData) return;
    try {
      const res = await api.post(
        `/cartoes/${activeCartao.id}/faturas/${pagarModalData.faturaReferencia}/pagar`,
        payload
      );
      toast.success(res.data?.message || 'Pagamento registrado!');
      const ref = pagarModalData.faturaReferencia;
      setPagarModalData(null);
      recarregarFatura(ref);
      // o saldo anterior aparece na fatura seguinte: atualiza a lista para ela existir na tela
      if (res.data?.saldoTransferido > 0) loadFaturas(activeCartao.id);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao registrar pagamento da fatura.');
      throw err;
    }
  };

  // Desfaz um pagamento parcial feito por engano e reabre o modal com os números atualizados
  const handleDesfazerPagamento = async (pagamento) => {
    if (!activeCartao || !pagarModalData) return;
    if (!window.confirm(`Desfazer o pagamento de ${formatCurrency(pagamento.valor)}? ${pagamento.carteiraNome ? 'A despesa gerada na conta também será removida.' : ''}`)) return;
    const ref = pagarModalData.faturaReferencia;
    try {
      await api.delete(`/cartoes/${activeCartao.id}/faturas/${ref}/pagamentos/${pagamento.id}`);
      toast.success('Pagamento desfeito.');
      const { data } = await api.get(`/cartoes/${activeCartao.id}/faturas/${ref}`);
      setPagarModalData(data);
      recarregarFatura(ref);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Não foi possível desfazer o pagamento.');
    }
  };

  // Totais consolidados
  const totalLimite = cartoes.reduce((acc, c) => acc + (c.limite || 0), 0);
  const totalUsado = cartoes.reduce((acc, c) => acc + (c.limiteUsado || 0), 0);
  const totalDisponivel = cartoes.reduce((acc, c) => acc + (c.limiteDisponivel || 0), 0);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6 page-enter pb-16 px-2 sm:px-4">
      {/* Header Compacto e Elegante */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
        <div>
          <h1
            className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2.5"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-sm">
              <CreditCard size={20} />
            </div>
            Cartões de Crédito
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Controle de limites, faturas agregadas, ciclos de fechamento e lançamentos
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => {
              setEditingCartao(null);
              setModalOpen(true);
            }}
            className="nexus-btn-primary flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold shadow-md cursor-pointer transition-all duration-150"
            data-testid="btn-novo-cartao"
          >
            <Plus size={16} />
            <span>Novo Cartão</span>
          </button>
        </div>
      </div>

      {/* KPI Cards Consolidados */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="fin-card p-4 flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Limite Total Somado
          </span>
          <div className="text-lg sm:text-xl font-bold text-slate-800 dark:text-white mt-1">
            {formatCurrency(totalLimite)}
          </div>
          <span className="text-[11px] text-slate-400 mt-1">
            {cartoes.length} cartão(ões) cadastrado(s)
          </span>
        </div>

        <div className="fin-card p-4 flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Limite Disponível
          </span>
          <div className="text-lg sm:text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
            {formatCurrency(totalDisponivel)}
          </div>
          <span className="text-[11px] text-slate-400 mt-1">
            Saldo livre para novas compras
          </span>
        </div>

        <div className="fin-card p-4 flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Limite Utilizado
          </span>
          <div className="text-lg sm:text-xl font-bold text-rose-600 dark:text-rose-400 mt-1">
            {formatCurrency(totalUsado)}
          </div>
          <span className="text-[11px] text-slate-400 mt-1">
            {totalLimite > 0 ? `${Math.round((totalUsado / totalLimite) * 100)}% de uso geral` : '0%'}
          </span>
        </div>

        <div className="fin-card p-4 flex flex-col justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Cartão Selecionado
          </span>
          <div className="text-sm sm:text-base font-bold text-indigo-600 dark:text-indigo-400 mt-1 truncate">
            {activeCartao ? activeCartao.nome : 'Nenhum'}
          </div>
          <span className="text-[11px] text-slate-400 mt-1">
            {activeCartao ? `Fecha dia ${activeCartao.diaFechamento} / Vence dia ${activeCartao.diaVencimento}` : 'Cadastre um cartão'}
          </span>
        </div>
      </div>

      {/* Qual cartão usar para comprar hoje */}
      {cartoes.length > 0 && <MelhorCartao cartoes={cartoes} />}

      {/* Grid de Cartões */}
      {cartoes.length === 0 && !loading ? (
        <div className="fin-card p-8 sm:p-12 text-center rounded-2xl border-dashed border-2 border-slate-200 dark:border-slate-800">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mx-auto mb-3 shadow-inner">
            <CreditCard size={32} />
          </div>
          <h3 className="text-base font-bold text-slate-800 dark:text-white mb-1">
            Nenhum cartão de crédito cadastrado
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-4">
            Cadastre seus cartões para acompanhar faturas por competência, controlar limites disponíveis e pagar faturas com baixa automática.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="nexus-btn-primary px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold inline-flex items-center gap-2"
          >
            <Plus size={16} />
            <span>Cadastrar Meu Primeiro Cartão</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cartoes.map((cartao) => {
            const isSelected = activeCartao?.id === cartao.id;
            const lim = cartao.limite || 0;
            const usado = cartao.limiteUsado || 0;
            const perc = lim > 0 ? Math.min(100, Math.round((usado / lim) * 100)) : 0;
            const progressColor = perc > 85 ? '#EF4444' : perc > 65 ? '#F59E0B' : '#10B981';

            return (
              <div
                key={cartao.id}
                onClick={() => setSelectedCartaoId(cartao.id)}
                className={`fin-card p-5 relative overflow-hidden transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                  isSelected
                    ? 'ring-2 ring-indigo-500 shadow-md transform -translate-y-0.5'
                    : 'hover:shadow-md'
                }`}
                style={{
                  borderLeft: `4px solid ${cartao.cor || '#6366F1'}`,
                }}
                data-testid={`card-cartao-${cartao.id}`}
              >
                {/* Top do Card */}
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm flex-shrink-0"
                        style={{ background: cartao.cor || '#6366F1' }}
                      >
                        <CreditCard size={20} />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">
                          {cartao.nome}
                        </h3>
                        <span className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                          <CardBrandIcon bandeira={cartao.bandeira} width={24} />
                          {bandeiraLabel(cartao.bandeira)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          setEditingCartao(cartao);
                          setModalOpen(true);
                        }}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                        title="Editar cartão"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => setDeletingCartao(cartao)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                        title="Excluir cartão"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Informações de Ciclo */}
                  <div className="flex items-center gap-3 mt-3.5 text-[11px] text-slate-500 dark:text-slate-400 font-medium bg-slate-50 dark:bg-slate-800/40 p-2 rounded-xl">
                    <div className="flex items-center gap-1">
                      <Clock size={12} className="text-indigo-500" />
                      <span>Fecha dia <b>{cartao.diaFechamento}</b></span>
                    </div>
                    <span className="text-slate-300 dark:text-slate-700">•</span>
                    <div className="flex items-center gap-1">
                      <Calendar size={12} className="text-emerald-500" />
                      <span>Vence dia <b>{cartao.diaVencimento}</b></span>
                    </div>
                  </div>

                  {/* Barra de Progresso do Limite */}
                  <div className="mt-4 space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-semibold">
                      <span className="text-slate-500 dark:text-slate-400">Limite Usado</span>
                      <span className="text-slate-800 dark:text-slate-200">
                        {formatCurrency(usado)} <span className="text-[10px] text-slate-400 font-normal">({perc}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${perc}%`, background: progressColor }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 pt-0.5">
                      <span>Disp: <b className="text-emerald-600 dark:text-emerald-400">{formatCurrency(cartao.limiteDisponivel)}</b></span>
                      <span>Total: {formatCurrency(lim)}</span>
                    </div>
                  </div>
                </div>

                {/* Ações Rápidas no Card */}
                <div className="pt-4 mt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCartaoId(cartao.id);
                    }}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-all ${
                      isSelected
                        ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300 font-extrabold'
                        : 'text-slate-600 dark:text-slate-400 hover:text-indigo-600'
                    }`}
                  >
                    <span>{isSelected ? 'Cartão Ativo' : 'Ver Faturas'}</span>
                    <ChevronRight size={13} />
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCartaoId(cartao.id);
                      setNovoLancamentoModalOpen(true);
                    }}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white bg-slate-800 dark:bg-slate-700 hover:bg-indigo-600 dark:hover:bg-indigo-600 transition-colors flex items-center gap-1"
                  >
                    <ShoppingBag size={12} />
                    <span>Nova Compra</span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCartaoId(cartao.id);
                      setImportFaturaOpen(true);
                    }}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-lg text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors flex items-center gap-1"
                    data-testid="btn-importar-fatura"
                  >
                    <FileUp size={12} />
                    <span>Importar fatura</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── PAINEL DE FATURAS DO CARTÃO SELECIONADO ─── */}
      {activeCartao && (
        <div className="fin-card p-5 sm:p-6 space-y-5 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-800">
          {/* Header do Painel */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm flex-shrink-0"
                style={{ background: activeCartao.cor || '#6366F1' }}
              >
                <CreditCard size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                    Faturas de {activeCartao.nome}
                  </h2>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                    <CardBrandIcon bandeira={activeCartao.bandeira} width={22} />
                    {bandeiraLabel(activeCartao.bandeira)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Fechamento dia {activeCartao.diaFechamento} • Vencimento dia {activeCartao.diaVencimento}
                </p>
              </div>
            </div>

            {/* Abas de Fatura (Abertas vs Fechadas) usando TabsNav unificado */}
            <div className="flex items-center gap-2">
              <TabsNav
                active={faturaTab}
                onChange={handleFaturaTabChange}
                accentColor={activeCartao.cor || '#6366F1'}
                tabs={[
                  {
                    id: 'abertas',
                    label: 'Faturas Abertas',
                    count: faturas.filter((f) => f.status === 'aberta').length,
                  },
                  {
                    id: 'fechadas',
                    label: 'Fechadas & Histórico',
                    count: faturas.filter((f) => f.status === 'fechada' || f.status === 'paga').length,
                  },
                ]}
              />
            </div>
          </div>

          {/* Sub-navegação: Pílulas de Meses / Faturas disponíveis */}
          {faturasFiltradas.length === 0 ? (
            <div className="p-8 text-center text-xs sm:text-sm text-slate-400">
              Nenhuma fatura encontrada nesta categoria.
            </div>
          ) : (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
              {faturasFiltradas.map((fat) => {
                const isSelected = selectedRef === fat.faturaReferencia;
                return (
                  <button
                    key={fat.faturaReferencia}
                    type="button"
                    onClick={() => setSelectedRef(fat.faturaReferencia)}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-150 flex items-center gap-2 flex-shrink-0 cursor-pointer ${
                      isSelected
                        ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm font-bold'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    <span>{formatMesReferencia(fat.faturaReferencia)}</span>
                    <span
                      className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                        fat.status === 'paga'
                          ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                          : fat.status === 'aberta'
                          ? 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400'
                          : 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                      }`}
                    >
                      {fat.status === 'paga' ? 'Paga' : fat.status === 'aberta' ? 'Aberta' : 'Fechada'}
                    </span>
                    <span className="text-[11px] opacity-80">
                      {formatCurrency(fat.total)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Detalhe da Fatura Selecionada */}
          {faturaDetalhe ? (
            <div className="space-y-4 pt-2">
              {/* Card Resumo da Fatura */}
              <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200/70 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Competência
                    </span>
                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                      {formatMesReferencia(faturaDetalhe.faturaReferencia)}
                    </span>
                    <span
                      className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                        faturaDetalhe.status === 'paga'
                          ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                          : faturaDetalhe.status === 'aberta'
                          ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                          : 'bg-amber-100 dark:bg-amber-950 text-amber-600 dark:text-amber-400'
                      }`}
                    >
                      {faturaDetalhe.status === 'paga'
                        ? `Paga ${faturaDetalhe.dataPagamento ? `em ${formatDate(faturaDetalhe.dataPagamento)}` : ''}`
                        : faturaDetalhe.status === 'aberta'
                        ? 'Fatura Aberta'
                        : 'Fechada — Aguardando Pagamento'}
                    </span>
                    {faturaDetalhe.parcial && (
                      <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-sky-100 dark:bg-sky-950 text-sky-600 dark:text-sky-400" data-testid="selo-parcial">
                        Parcialmente paga
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 dark:text-slate-400">
                    <span>Fechamento: <b>{formatDate(faturaDetalhe.dataFechamento)}</b></span>
                    <span>•</span>
                    <span>Vencimento: <b>{formatDate(faturaDetalhe.dataVencimento)}</b></span>
                    <span>•</span>
                    <span>{faturaDetalhe.qtdLancamentos} lançamento(s)</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block">
                      Total da Fatura
                    </span>
                    <span className="text-2xl font-extrabold text-slate-900 dark:text-white">
                      {formatCurrency(faturaDetalhe.total)}
                    </span>
                    {faturaDetalhe.parcial && (
                      <span className="block text-[11px] mt-0.5 text-slate-500 dark:text-slate-400" data-testid="resumo-parcial">
                        Pago <b className="text-emerald-600">{formatCurrency(faturaDetalhe.totalPago)}</b> · Falta <b>{formatCurrency(faturaDetalhe.restante)}</b>
                      </span>
                    )}
                    {faturaDetalhe.saldoTransferido > 0 && (
                      <span className="block text-[11px] mt-0.5 text-amber-600 dark:text-amber-400" data-testid="resumo-transferido">
                        {formatCurrency(faturaDetalhe.saldoTransferido)} foram para a fatura de {formatMesReferencia(faturaDetalhe.proximaReferencia)}
                      </span>
                    )}
                  </div>

                  {/* Ação de Pagamento */}
                  {faturaDetalhe.status !== 'paga' && faturaDetalhe.total > 0 && (
                    <button
                      type="button"
                      onClick={() => setPagarModalData(faturaDetalhe)}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <CheckCircle2 size={16} />
                      <span>Pagar Fatura</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Tabela de Lançamentos da Fatura */}
              <div className="overflow-x-auto rounded-xl border border-slate-200/80 dark:border-slate-800">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100/80 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="py-2.5 px-3">Data</th>
                      <th className="py-2.5 px-3">Descrição</th>
                      <th className="py-2.5 px-3">Categoria</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                    {faturaDetalhe.lancamentos?.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-6 text-center text-slate-400">
                          Nenhum lançamento registrado nesta fatura ainda.
                        </td>
                      </tr>
                    ) : (
                      faturaDetalhe.lancamentos.map((lanc) => (
                        <tr
                          key={lanc.id}
                          className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                        >
                          <td className="py-2.5 px-3 whitespace-nowrap text-slate-500 dark:text-slate-400 font-medium">
                            {formatDate(lanc.dataCompetencia)}
                          </td>
                          <td className="py-2.5 px-3 font-semibold text-slate-800 dark:text-slate-100">
                            {lanc.descricao}
                          </td>
                          <td className="py-2.5 px-3 text-slate-500 dark:text-slate-400">
                            {lanc.categoria || 'Geral'}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                lanc.status === 'pago'
                                  ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                                  : 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'
                              }`}
                            >
                              {lanc.status === 'pago' ? 'Pago' : 'Pendente'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right font-bold text-rose-600 dark:text-rose-400 whitespace-nowrap">
                            {formatCurrency(lanc.valor)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : detalheLoading ? (
            <div className="py-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <RefreshCw size={14} className="animate-spin" />
              <span>Carregando detalhes da fatura...</span>
            </div>
          ) : null}
        </div>
      )}

      {/* ── MODAIS ── */}
      <CartaoModal
        isOpen={modalOpen}
        initial={editingCartao}
        carteiras={carteiras}
        onSave={handleSaveCartao}
        onClose={() => {
          setModalOpen(false);
          setEditingCartao(null);
        }}
      />

      <PagarFaturaModal
        isOpen={Boolean(pagarModalData)}
        cartao={activeCartao}
        fatura={pagarModalData}
        carteiras={carteiras}
        onConfirm={handlePagarFatura}
        onDesfazer={handleDesfazerPagamento}
        onClose={() => setPagarModalData(null)}
      />

      {/* Modal de Exclusão */}
      {deletingCartao && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4"
          style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => setDeletingCartao(null)}
        >
          <div
            className="nexus-modal w-full max-w-sm p-5 sm:p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl space-y-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-2xl bg-rose-100 dark:bg-rose-950/60 text-rose-600 flex items-center justify-center mx-auto">
              <Trash2 size={24} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                Excluir Cartão de Crédito?
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Deseja realmente remover o cartão <b>{deletingCartao.nome}</b>?
                Não será possível excluir caso existam lançamentos vinculados.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeletingCartao(null)}
                className="px-4 py-2 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDeleteCartao}
                className="px-4 py-2 text-xs font-bold rounded-xl text-white bg-rose-600 hover:bg-rose-700 shadow-md"
              >
                Sim, Excluir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Importação de fatura (CSV do banco) */}
      <ImportarFaturaModal
        open={importFaturaOpen}
        cartao={activeCartao}
        referenciaInicial={selectedRef}
        onClose={() => setImportFaturaOpen(false)}
        onImported={() => {
          loadCartoes();
          if (activeCartao) {
            loadFaturas(activeCartao.id);
            if (selectedRef) loadFaturaDetalhe(activeCartao.id, selectedRef);
          }
        }}
      />

      {/* Modal de Novo Lançamento pré-selecionado para este Cartão */}
      <LancamentoModal
        isOpen={novoLancamentoModalOpen}
        onClose={() => setNovoLancamentoModalOpen(false)}
        lancamento={activeCartao ? {
          tipo: 'DESPESA',
          formaPagamento: 'Cartão',
          cartaoId: activeCartao.id,
        } : null}
        onSuccess={() => {
          loadCartoes();
          if (activeCartao) {
            loadFaturas(activeCartao.id);
            if (selectedRef) loadFaturaDetalhe(activeCartao.id, selectedRef);
          }
        }}
      />
    </div>
  );
}
